import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import partition from 'lodash/partition';
import pick from 'lodash/pick';

import {SESSION_EVENTS} from 'common/report/constants';
import {renameEvent} from 'common/report/util';
import FullLengthDateRange from 'common/widgets/date-range-picker/models/full-length-date-range';
import PropertyFilter from 'common/widgets/property-filter-menu/models/property-filter';
import {
  convertPropertyFilterToSegfilter,
  convertSegfilterToPropertyFilter,
  SegFilterProps,
  Segfilter,
} from 'common/widgets/property-filter-menu/models/segfilter';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {Segment} from 'common/widgets/query-block/segment-menu/types';
import {
  bookmarkGroupByToGroupBy,
  groupByToBookmarkGroupBy,
  isGroupByEqual,
} from 'common/widgets/query-block/segment-menu/util';
import {QueryEntry, FilterOperator} from 'common/widgets/query-event-selector/types';
import {EventItem} from 'common/widgets/query-block/events-menu/types';
import {getFilterByCohorts, bookmarkFilterByCohortToCohortFilters} from 'common/widgets/query-block/filter-menu/util';
import CustomEventPropertyFilter from 'common/widgets/property-filter-menu/models/custom-event-property-filter';

import {FunnelBookmark, FunnelBookmarkStep} from './types';

// TODO: This file should mostly be ignored for now. It was added for the Flows query builder project
// in order to allow Flows to be unblocked as Funnels underwent some changes and is only a subset of what is needed in the full funnels app.
// We will be merging code soon to avoid duplicated code and any confusion both here and in classes used here,
// such as in types.ts (the FunnelBookmark and FunnelBookmarkStep interfaces)

export const DEFAULT_VALUES: Partial<FunnelsBookmark> = Object.freeze({
  cohortFilters: [],
  dataGroupId: null,
  dateRange: new FullLengthDateRange().getAttributes(),
  eventFilters: [],
  events: [],
  exclusionSteps: [],
  groupBy: [],
  holdingPropertyConstant: [],
  projectID: -1,
  querySampling: false,
  userFilters: [],
  utcOffset: 0,
});

/** Instantiates a PropertyFilter class from attributes */
const constructPropertyFilters = (filterPrimitives) => filterPrimitives.map((filter) => new PropertyFilter(filter));

/**
 * Determine if two lists of filters are equivalent
 */
const areAllFiltersEqual = (filterListA: PropertyFilter[], filterListB: PropertyFilter[], _: boolean): boolean =>
  filterListA.length === filterListB.length && filterListA.every((filter, index) => filter.equals(filterListB[index]));

/**
 * Determine if two event entries are eqivalent
 * @param {boolean} forQuery If it results in a difference in the query & results.
 */
const eventOmitProperties = (forQuery) => [`name`, ...(forQuery ? [`renamed`] : [])];
const areEventsEqual = (eventA: QueryEntry, eventB: QueryEntry, forQuery: boolean): boolean =>
  eventA &&
  eventB &&
  (eventA.filters.length < 2 || eventA.filtersOperator === eventB.filtersOperator) &&
  (eventA.eventIndexLabelToggled || false) === (eventB.eventIndexLabelToggled || false) &&
  isEqual(omit(eventA.event, eventOmitProperties(forQuery)), omit(eventB.event, eventOmitProperties(forQuery))) &&
  areAllFiltersEqual(eventA.filters, eventB.filters, forQuery);

const deepEquals = (itemA, itemB, _: boolean): boolean => isEqual(itemA, itemB);
const EQUIVALENCY_EVALUATORS: {[key: string]: (a: any, b: any, forQuery: boolean) => boolean} = Object.freeze({
  cohortFilters: areAllFiltersEqual,
  conversionWindow: deepEquals,
  countType: (a, b, _) => a === b,
  // dataGroupId equality can collapse to isEqual if we make sure it is never an empty string
  dataGroupId: (a, b, _) => (!a && !b) || a === b,
  dateRange: (a, b, _) => {
    if (!(a && b)) {
      return a === b;
    } // bail out if one is false-y
    return a.equals(b);
  },
  eventFilters: areAllFiltersEqual,
  events: (a, b, forQuery) =>
    a.length === b.length && a.every((entry, eventIndex) => areEventsEqual(entry, b[eventIndex], forQuery)),
  exclusionSteps: (a, b, forQuery) =>
    a.length === b.length &&
    a.every(
      (entry, eventIndex) =>
        areEventsEqual(entry, b[eventIndex], forQuery) &&
        entry.excludeAll === b[eventIndex].excludeAll &&
        isEqual(entry.excludeRange, b[eventIndex].excludeRange),
    ),
  groupBy: isGroupByEqual,
  controlGroup: (a, b, _) => isEqual(a, b),
  holdingPropertyConstant: deepEquals,
  userFilters: areAllFiltersEqual,
  timeBuckets: deepEquals,
  trendUnit: (a, b, _) => a === b,
  funnelOrder: (a, b, _) => a === b,
  /** querySampling is not saved and only affects query results */
  querySampling: (a, b, forQuery) => (forQuery ? a === b : true),
  chartType: (a, b) => a === b,
  eventIndex: (a, b, forQuery) => forQuery || a === b,
});

export class FunnelsBookmark {
  /**
   * Convert from bookmark to funnel query, reverse of "generateBookmark"
   * @param params bookmark params
   * @param customEvents ist of supported custom events.
   */
  public static fromBookmark(params) {
    const attrs = FunnelsBookmark.bookmarkToClassParams(params);
    return new FunnelsBookmark(attrs);
  }

  public static bookmarkToClassParams(params) {
    const attrs: Partial<FunnelsBookmark> = {};

    if (params.steps) {
      attrs.events = FunnelsBookmark.bookmarkStepsToEvents(params.steps);
    }

    if (params.exclusions) {
      attrs.exclusionSteps = FunnelsBookmark.bookmarkExclusionsToExclusionSteps(params.exclusions, attrs.events.length);
    }

    if (params.group_by) {
      attrs.groupBy = bookmarkGroupByToGroupBy(params.group_by);
    }

    // TODO: Split this into cohortFilter and userFilters when we switch over to the new API
    attrs.cohortFilters = bookmarkFilterByCohortToCohortFilters(params.filter_by_cohort);

    if (params.date_range) {
      attrs.dateRange = FullLengthDateRange.fromBookmarkFormat(params.date_range).getAttributes();
    }

    if (params.filter_by_event) {
      const filters = partition(params.filter_by_event.children, `event`);
      attrs.eventFilters = filters[0].map((filter) => filter.event);
      attrs.userFilters = filters[1].map((filter) => filter.user);
    }

    if (params.data_group_id) {
      attrs.dataGroupId = params.data_group_id;
    }

    return attrs;
  }

  /**
   * Transforms bookmark exclusion steps to UI state events
   * and determines the type of exclusion for each step
   */
  private static bookmarkExclusionsToExclusionSteps(
    exclusionSteps: FunnelBookmarkStep[],
    eventCount: number,
  ): QueryEntry[] {
    const exclusions = FunnelsBookmark.bookmarkStepsToEvents(exclusionSteps);

    return exclusions.map((step) => {
      const {from, to} = step.excludeRange;
      let exclusionAttributes = {};

      if (from === 1 && to === eventCount) {
        exclusionAttributes = {excludeAll: true, excludeRange: {}};
      } else {
        exclusionAttributes = {excludeAll: false, excludeRange: step.excludeRange};
      }

      return {
        ...step,
        ...exclusionAttributes,
      };
    });
  }

  /**
   * Transforms bookmark steps to UI state events
   */
  private static bookmarkStepsToEvents(steps: FunnelBookmarkStep[]): QueryEntry[] {
    return steps.map((step) => {
      const additionalAttributes: Partial<QueryEntry> = {};
      const isCustomEvent = step.hasOwnProperty(`custom_event`);

      let stepFilters: Segfilter[] = [];
      if (step.property_filter_params_list) {
        stepFilters = step.property_filter_params_list;
      } else if (step.filter) {
        // // Older bookmarks don't have property filter params list,
        // // instead they have just one filter at the top level.
        const stepFilter = pick(step, SegFilterProps) as Segfilter;
        if (typeof stepFilter.property === `string`) {
          // Some older bookmarks have even less information that needs to be translated.
          stepFilter.property = {
            name: stepFilter.property,
            type: stepFilter.type,
            source: `properties`,
          };
        }
        stepFilters = [stepFilter];
      }

      const filters = stepFilters.map((filter) =>
        convertSegfilterToPropertyFilter(filter).getAttributes(),
      ) as PropertyFilter[];

      // convert exclusion steps
      if (step.steps) {
        additionalAttributes.excludeRange = step.steps;
      }

      // determine label of the event
      // older funnels bookmarks don't store all the necessary information in them. So we need to guess what the current
      // non renamed label would be, and we consider it renamed if the stored label doesn't match that.
      const eventLabel = step.step_label || ``;
      const sessionEventKey =
        step.session_event &&
        Object.keys(SESSION_EVENTS).find((session) => SESSION_EVENTS[session][`session_event`] === step.session_event);
      let eventName;
      if (sessionEventKey) {
        eventName = renameEvent(sessionEventKey);
      } else {
        eventName = renameEvent(step.event);
      }

      // also check against step.event for older bookmarks before we performed renaming for newer properties.
      const isRenamed = eventLabel !== step.event && eventLabel !== eventName;

      return {
        ...additionalAttributes,
        filters,
        filtersOperator: (step.bool_op || FilterOperator.Or) as FilterOperator,
        type: ResourceType.Event,
        event: {
          ...(isCustomEvent ? {id: step.custom_event} : {}),
          custom: isCustomEvent,
          label: eventName,
          name: sessionEventKey || step.event || step.custom_event_name,
          value: sessionEventKey || step.event || step.custom_event_name,
          ...(isRenamed ? {renamed: eventLabel} : {}),
        },
      };
    });
  }

  /**
   * Parses filter by cohorts
   * Definition moved to `iron/common/widgets/query-block/filter-menu/util.ts`.
   */
  public version: number;
  public dataGroupId: string;
  public dateRange: Partial<FullLengthDateRange>;
  public cohortFilters: PropertyFilter[];
  public eventFilters: PropertyFilter[];
  public events: QueryEntry[];
  public exclusionSteps: QueryEntry[];
  public groupBy: Segment[];

  /** transient, should not be persisted */
  public querySampling: boolean;
  public userFilters: PropertyFilter[];

  /**
   * @param [attrs] - Attributes to set on the new FunnelsBookmark object
   */
  constructor(attrs: Partial<FunnelsBookmark> = {}) {
    Object.assign(this, DEFAULT_VALUES, attrs);

    // Instantiate models from primitive attrs
    this.dateRange = new FullLengthDateRange(this.dateRange);
    this.events = this.events.map((entry) => ({...entry, filters: constructPropertyFilters(entry.filters)}));
    this.exclusionSteps = this.exclusionSteps.map((entry) => ({
      ...entry,
      filters: constructPropertyFilters(entry.filters),
    }));

    // force filter initialization
    // eslint-disable-next-line no-self-assign
    this.filters = this.filters;
  }

  get eventSelection() {
    return this.events.filter(Boolean);
  }

  get filters(): PropertyFilter[] {
    return [...this.eventFilters, ...this.userFilters, ...this.cohortFilters];
  }

  set filters(filters) {
    this.eventFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Event));
    this.userFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.User));
    this.cohortFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Cohort));
  }

  /**
   * Determine if this funnels query is equal to another funnels query
   * @param otherQuery The other query to compare this query to
   * @param forQuery
   */
  public equals(otherQuery: FunnelsBookmark, forQuery = false) {
    const evaluators = Object.keys(EQUIVALENCY_EVALUATORS).filter(
      (attr) => this.hasOwnProperty(attr) && otherQuery.hasOwnProperty(attr),
    );

    return evaluators.every((attr) => EQUIVALENCY_EVALUATORS[attr](this[attr], otherQuery[attr], forQuery));
  }

  /**
   * Determine if this funnels query is equal to another funnels query in terms of requiring a re-query.
   * (i.e. will it generate a query that will return different results)
   * @param otherQuery The other query to compare this query to
   */
  public generatedQueryEquals(otherQuery) {
    return this.equals(otherQuery, true);
  }

  /**
   * Generate a bookmark object that can make a query and be persisted as a saved report
   */
  public generateBookmark({includeNegatedCohort = true} = {}): FunnelBookmark {
    const steps = this.eventsToBookmarkSteps(this.eventSelection);
    const exclusions = steps.length > 1 ? this.eventsToBookmarkSteps(this.exclusionSteps) : [];
    const groupBy = groupByToBookmarkGroupBy(this.groupBy, includeNegatedCohort);

    const params: FunnelBookmark = {
      ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),
      steps,
      exclusions,
      date_range: this.dateRange.getBookmarkFormat(),
      filter_by_cohort: JSON.stringify(this.cohortFilters.length ? getFilterByCohorts(this.cohortFilters) : {}),
      version: this.version,
    };

    if (!isEmpty(groupBy)) {
      params.group_by = groupBy;
    }

    // TODO: Remove userFilters length check once we switch to the new API
    if (this.eventFilters.length > 0 || this.userFilters.length > 0) {
      params.filter_by_event = this.getFilterByEvent();
    }

    return params;
  }

  public getFilterByEvent() {
    // TODO: Remove userFilter merge from this once we switch over to the new API
    return {
      operator: `and`,
      children: [
        ...this.eventFilters.map((filter) => ({event: filter})),
        ...this.userFilters.map((filter) => ({user: filter})),
      ],
    };
  }

  /**
   * Transforms UI state events to bookmark steps
   */
  private eventsToBookmarkSteps(events: QueryEntry[]): FunnelBookmarkStep[] {
    events = events.filter((entry) => entry && entry.event.value);

    return events.map((step) => {
      const {filters, filtersOperator} = step;
      const event = step.event as EventItem;
      const segFilters = filters.map((filter) => convertPropertyFilterToSegfilter(filter as CustomEventPropertyFilter));
      let eventProperties: Partial<FunnelBookmarkStep> = {
        step_label: event.renamed || event.label,
        ...pick(step, `forward`, `reverse`),
      };

      // generate event properties
      if (event.custom) {
        eventProperties = {
          ...eventProperties,
          custom_event: event.id,
          custom_event_name: event.value,
        };
      } else if (SESSION_EVENTS[event.value]) {
        eventProperties = {
          ...SESSION_EVENTS[event.value],
          ...eventProperties,
        };
      } else {
        eventProperties = {
          ...eventProperties,
          event: event.value,
        };
      }

      // generate exclusion step ranges
      if (step.excludeRange && step.excludeRange.from) {
        eventProperties.steps = {...step.excludeRange};
      } else if (step.excludeAll) {
        const filledEvents = this.events.filter((entry) => entry.event.value);
        eventProperties.steps = {from: 1, to: filledEvents.length};
      }

      return {
        ...eventProperties,
        bool_op: filtersOperator,
        property_filter_params_list: segFilters,
      };
    });
  }
}
